/**
 * ============================================================================
 * [P3-02-F] SocialMetricsChart - 소셜 메트릭 차트 (레거시 복구)
 *
 * 기능:
 * - 포스트들의 조회수, 좋아요, 댓글, 공유, 팔로우 추이 라인 차트
 * - 모드 전환: 전체 합산(total) vs 개별 포스트(individual)
 * - 축 스케일링: 통합(combined) vs 분리(split)
 * - 표시 기간: 최근 7일(7d), 30일(30d), 전체(all)
 * ============================================================================
 */

import { useState, useMemo } from 'react'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js'
import { Line } from 'react-chartjs-2'

import { useTrackingChart } from '@/hooks/useTrackingChart'
import type { TrackingPost, ChartConfig, ChartMode, ChartRange, ChartScaleMode } from '@/types'
import styles from './Tracking.module.css'

// ============================================================================
// Chart.js 플러그인 등록
// ============================================================================
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
)

interface SocialMetricsChartProps {
  trackingPosts: TrackingPost[]
}

export function SocialMetricsChart({ trackingPosts }: SocialMetricsChartProps) {
  // ========================================================================
  // 차트 설정 상태
  // ========================================================================
  const [mode, setMode] = useState<ChartMode>('total')
  const [range, setRange] = useState<ChartRange>('7d')
  const [scaleMode, setScaleMode] = useState<ChartScaleMode>('combined')
  const [selectedPostId, setSelectedPostId] = useState<string>('')

  const config: ChartConfig = useMemo(
    () => ({
      mode,
      range,
      scaleMode,
      selectedPostId: selectedPostId || undefined,
    }),
    [mode, range, scaleMode, selectedPostId]
  )

  // ========================================================================
  // 데이터 가공 훅
  // ========================================================================
  const { labels, datasets } = useTrackingChart(trackingPosts, config)

  // ========================================================================
  // 차트 옵션 구성 (split 축 지원)
  // ========================================================================
  const chartOptions = useMemo(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const scales: any = {
      x: {
        grid: { display: false },
      },
      y: {
        beginAtZero: true,
        type: 'linear',
        display: true,
        position: 'left',
        ticks: { precision: 0 },
      },
    }

    if (scaleMode === 'split') {
      scales.y2 = {
        beginAtZero: true,
        type: 'linear',
        display: true,
        position: 'right',
        grid: { drawOnChartArea: false }, // 오른쪽 축의 그리드는 숨김 처리
        ticks: { precision: 0 },
      }
    }

    return {
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        mode: 'index' as const,
        intersect: false,
      },
      plugins: {
        legend: {
          display: true,
          position: 'bottom' as const,
        },
        tooltip: {
          enabled: true,
        },
      },
      scales,
    }
  }, [scaleMode])

  // ========================================================================
  // Render
  // ========================================================================
  return (
    <div className={styles.chartContainer}>
      <div className={styles.chartHeaderBlock} style={{ marginBottom: '16px' }}>
        <h3 style={{ margin: '0 0 12px 0', fontSize: '1.2rem', color: '#1a202c' }}>포스트 성과 추이</h3>
        
        {/* 컨트롤 바 */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
            {/* 모드 전환 */}
            <div style={{ display: 'flex', background: '#f1f5f9', borderRadius: '6px', padding: '2px' }}>
              <button
                style={{
                  padding: '6px 12px', border: 'none', borderRadius: '4px', fontSize: '0.85rem',
                  cursor: 'pointer', fontWeight: mode === 'total' ? 'bold' : 'normal',
                  background: mode === 'total' ? 'white' : 'transparent',
                  color: mode === 'total' ? '#667eea' : '#64748b',
                  boxShadow: mode === 'total' ? '0 1px 2px rgba(0,0,0,0.05)' : 'none'
                }}
                onClick={() => setMode('total')}
              >전체 누적</button>
              <button
                style={{
                  padding: '6px 12px', border: 'none', borderRadius: '4px', fontSize: '0.85rem',
                  cursor: 'pointer', fontWeight: mode === 'individual' ? 'bold' : 'normal',
                  background: mode === 'individual' ? 'white' : 'transparent',
                  color: mode === 'individual' ? '#667eea' : '#64748b',
                  boxShadow: mode === 'individual' ? '0 1px 2px rgba(0,0,0,0.05)' : 'none'
                }}
                onClick={() => setMode('individual')}
              >개별 포스트</button>
            </div>

            {/* 개별 포스트 선택 (개별 모드일 때만) */}
            {mode === 'individual' && (
              <select
                value={selectedPostId}
                onChange={(e) => setSelectedPostId(e.target.value)}
                style={{
                  padding: '6px 10px', borderRadius: '6px', border: '1px solid #e2e8f0', fontSize: '0.85rem', maxWidth: '200px'
                }}
              >
                <option value="">-- 포스트 선택 --</option>
                {trackingPosts.map(p => (
                  <option key={p.id} value={p.id}>
                    {p.title || (p.content ? p.content.substring(0, 20) + '...' : '(내용 없음)')}
                  </option>
                ))}
              </select>
            )}
          </div>

          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
            {/* 기간 범위 */}
            <div style={{ display: 'flex', background: '#f1f5f9', borderRadius: '6px', padding: '2px' }}>
              {(['7d', '30d', 'all'] as ChartRange[]).map((r) => (
                <button
                  key={r}
                  style={{
                    padding: '4px 10px', border: 'none', borderRadius: '4px', fontSize: '0.8rem',
                    cursor: 'pointer', fontWeight: range === r ? 'bold' : 'normal',
                    background: range === r ? 'white' : 'transparent',
                    color: range === r ? '#4a5568' : '#718096',
                    boxShadow: range === r ? '0 1px 2px rgba(0,0,0,0.05)' : 'none'
                  }}
                  onClick={() => setRange(r)}
                >
                  {r === '7d' ? '7일' : r === '30d' ? '30일' : '전체'}
                </button>
              ))}
            </div>

            {/* 축 스케일링 */}
            <div style={{ display: 'flex', background: '#f1f5f9', borderRadius: '6px', padding: '2px' }}>
              {(['combined', 'split'] as ChartScaleMode[]).map((s) => (
                <button
                  key={s}
                  style={{
                    padding: '4px 10px', border: 'none', borderRadius: '4px', fontSize: '0.8rem',
                    cursor: 'pointer', fontWeight: scaleMode === s ? 'bold' : 'normal',
                    background: scaleMode === s ? 'white' : 'transparent',
                    color: scaleMode === s ? '#4a5568' : '#718096',
                    boxShadow: scaleMode === s ? '0 1px 2px rgba(0,0,0,0.05)' : 'none'
                  }}
                  onClick={() => setScaleMode(s)}
                >
                  {s === 'combined' ? '통합 축' : '분리 축'}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* 차트 영역 */}
      <div className={styles.chartWrapper} style={{ height: '300px', width: '100%', position: 'relative' }}>
        {labels.length > 0 ? (
          <Line 
            data={{ 
              labels, 
              datasets: datasets.map((d, i) => ({
                ...d, 
                fill: false, 
                tension: 0.4, 
                pointRadius: 4, 
                pointHoverRadius: 6,
                yAxisID: scaleMode === 'split' ? (i===0 ? 'y' : 'y2') : 'y'
              }))
            }} 
            options={chartOptions} 
          />
        ) : (
          <div style={{ display: 'flex', height: '100%', alignItems: 'center', justifyContent: 'center', color: '#a0aec0' }}>
            데이터가 없습니다.
          </div>
        )}
      </div>
    </div>
  )
}

export default SocialMetricsChart
